import * as Sentry from '@sentry/browser';
import { useCookies } from 'react-cookie';

import { EmbedMapDetailData } from '@type-def_iqloud/EmbedMapDetailData';
import { CreateEditCloudMapType, CreateEditFreeMapType } from '@type-def_iqloud/MapType';
import { SearchEmbedMapsQuery } from '@type-def_iqloud/SearchEmbedMapsQuery';

const backendBasePath = process.env.NEXT_PUBLIC_DEV_BACKEND_URL;

export const useMapData = () => {
  const [cookies] = useCookies(['expiry', 'access-token', 'client', 'uid', 'user-type']);

  const getEmbedMapDetail = async (embed_map_id: string): Promise<EmbedMapDetailData> => {
    const url = `${backendBasePath}/embed_maps/${embed_map_id}`;
    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getEmbedMaps = async (query: SearchEmbedMapsQuery) => {
    if (query.offset < 0) query.offset = 0;
    const url = `${backendBasePath}/admin/embed_maps?name=${query.mapName}&company_id=${query.companyId}&offset=${query.offset}&limit=${query.limit}`;
    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getEmbedMapInfo = async (embed_map_id: string) => {
    const url = `${backendBasePath}/admin/embed_maps/${embed_map_id}`;
    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const createMap = async (mapData: CreateEditCloudMapType) => {
    const url = `${backendBasePath}/admin/embed_maps`;
    let status;
    const result = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      body: JSON.stringify(mapData),
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
    })
      .then(async (response) => {
        if (response.status === 200) {
          status = 'success';
          return response.json();
        } else {
          status = 'error';
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });

    return { status: status, result: result };
  };

  const updateMap = async (mapData: CreateEditCloudMapType | CreateEditFreeMapType, embed_map_id: string) => {
    const url = `${backendBasePath}/admin/embed_maps/${embed_map_id}`;
    let status;
    const result = await fetch(url, {
      method: 'PATCH',
      mode: 'cors',
      body: JSON.stringify(mapData),
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
    })
      .then(async (response) => {
        if (response.status === 200) {
          status = 'success';
          return response.json();
        } else {
          status = 'error';
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });

    return { status: status, result: result };
  };

  const deleteMap = async (embed_map_id: string) => {
    const url = `${backendBasePath}/admin/embed_maps/${embed_map_id}`;

    const result = await fetch(url, {
      method: 'DELETE',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });

    return result;
  };

  const createFreeMap = async (company_id: string) => {
    const url = `${backendBasePath}/admin/embed_maps/create_free_map`;
    let status;
    const result = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      body: JSON.stringify({ company_id }),
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
    })
      .then(async (response) => {
        if (response.status === 200) {
          status = 'success';
          return response.json();
        } else {
          status = 'error';
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });

    return { status, result };
  };

  return {
    getEmbedMapDetail,
    getEmbedMaps,
    getEmbedMapInfo,
    createMap,
    updateMap,
    deleteMap,
    createFreeMap,
  };
};
